import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/core/auth/useAuth';

/**
 * The tenant the admin UI is currently operating on.
 *
 * Only `empresa` (Zerep staff) can point this at a company other than their
 * own; for every other role it is pinned to `user.company_id` and the switcher
 * is never rendered. The value is mirrored into module scope so the axios
 * interceptor can attach it as `X-Company-Id` without subscribing to React.
 */
interface TenantContextValue {
  companyId: string | null;
  setCompanyId: (id: string) => void;
  /** True when the active tenant is not the user's own company. */
  isImpersonating: boolean;
  canSwitch: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

const STORAGE_KEY = 'fsmops.activeCompanyId';

/** Read by the axios interceptor — see core/http/client.ts. */
let activeCompanyId: string | null = null;
export function getActiveCompanyId(): string | null {
  return activeCompanyId;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const canSwitch = user?.role === 'empresa';

  const [companyId, setCompanyIdState] = useState<string | null>(null);

  // Non-Zerep roles are always pinned to their own company. Zerep restores its
  // last selection, falling back to Zerep itself.
  useEffect(() => {
    if (!user) {
      activeCompanyId = null;
      setCompanyIdState(null);
      return;
    }
    if (!canSwitch) {
      activeCompanyId = user.company_id;
      setCompanyIdState(user.company_id);
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    const next = stored ?? user.company_id;
    activeCompanyId = next;
    setCompanyIdState(next);
  }, [user, canSwitch]);

  const value = useMemo<TenantContextValue>(
    () => ({
      companyId,
      canSwitch,
      isImpersonating: Boolean(user && companyId && companyId !== user.company_id),
      setCompanyId: (id: string) => {
        if (!canSwitch) return;
        activeCompanyId = id;
        localStorage.setItem(STORAGE_KEY, id);
        setCompanyIdState(id);
      },
    }),
    [companyId, canSwitch, user],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within a TenantProvider');
  return ctx;
}
